import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { isServer } from '../lib/utils';
import { selectLocation } from '../store/location/selectors';

/**
 * There is an observed behavior of redux-first-router, in which, upon
 * navigating from one route to another, the component associated with
 * the old route is rendered with the new route's "location" state,
 * prior to the new component being rendered. This can be exacerbated
 * by usage of dispatching events in redux-first-router onBeforeChange
 * configuration option.
 *
 * For routes that depend on location path or query params, this behavior
 * can wreak havoc because such params will be absent (or worse, different)
 * than those expected by whatever operations may exist in the component
 * (such as loading an order by id).
 *
 * This hook is intended for components that are tied to specific
 * location pathnames. It reports when the pathname has changed from
 * the initial value, a signal that the component is in this specific
 * condition of rendering during a navigation state change.
 *
 * For more details, see {@link https://github.com/faceyspacey/redux-first-router/issues/475|this issue}.
 *
 * @returns {boolean} Whether the pathname changed from the value present
 * at component mount-time. It can be used to short circuit rendering
 * with a <Loading> component, for example (not ideal, but probably good
 * enough as a placeholder to work around this condition).
 */
export const usePathnameChanged = () => {
  const location = useSelector(selectLocation);
  const current = location.pathname;
  const [initial, setInitial] = useState(null);

  useEffect(() => {
    if (!isServer && !initial) setInitial(current);
  }, [current, initial, setInitial]);

  const isChanged = initial && initial !== current;
  return isChanged;
};
